import {
  Box,
  TextField,
  Typography,
  Link as MUILink,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Paper,
  CircularProgress,
  Snackbar,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useState } from "react";
import { inputHelper} from "../../Helper";
import { useLoginUserMutation } from "../../Apis/authApi";
import { jwtDecode } from "jwt-decode";
import { userModel } from "../../Interfaces";
import { useDispatch } from "react-redux";
import { setLoggedInUser } from "../../Storage/Redux/userAuthSlice";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";


let logo2 = require("../../Assets/Images/logoDEC.png");

export default function Login() {
  const [validated, setValidated] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
  const [loginUser] = useLoginUserMutation();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const [userInput, setUserInput] = useState({
    userName: "",
    password: "",
    rememberMe: true,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput((prevState) => ({
      ...prevState,
      rememberMe: e.target.checked,
    }));
  };

  const handleUserInput = (e: any) => {
    const tempData = inputHelper(e, userInput);
    setUserInput(tempData);
    setValidated(false);
  };
    const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();    
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    setValidated(true);
    setLoading(true);

    try {
      const payload = {
        userName: userInput.userName,
        password: userInput.password,
        rememberMe: userInput.rememberMe,
      };

      const response = await loginUser(payload).unwrap();

      const { token } = response.result;
      const { fullName, nameid, email, role }: userModel = jwtDecode(token);

      if (token) {
        localStorage.setItem("token", token);
        dispatch(setLoggedInUser({ fullName, nameid, email, role }));
        navigate("/");
      }
    } catch (error: any) {
      if (error.data && error.data.errorMessages) {
        setSnackbar({
          open: true,
          message: error.data.errorMessages[0],
          severity: "error",
        });
        
      } else {
        setSnackbar({
          open: true,
          message: error.data?.errorMessages?.[0] || "An unexpected error occurred",
          severity: "error",
        });
        
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: "83.33%", md: "50%", lg: "33.33%" },
          maxWidth: 400,
          mx: "auto",
        }}
      >
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
          {/* Header */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
              borderBottom: "2px solid rgba(81, 35, 132, 1)",
            }}
          >
            <Box
              component="img"
              src={logo2}
              alt="Logo"
              sx={{
                maxHeight: 40,
                display: "inline-block",
                pb: 0.5, // small space between image and underline
              }}
            />
          </Box>

          {/* Error Alert */}

          <Typography
            variant="h5"
            textAlign="left"
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            Login
          </Typography>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              required
              label="Email"
              name="userName"
              value={userInput.userName}
              onChange={handleUserInput}
              error={validated && !userInput.userName}
              helperText={
                validated && !userInput.userName
                  ? "Please Enter Your Email."
                  : ""
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              label="Password"
              name="password"
              value={userInput.password}
              onChange={handleUserInput}
              error={validated && !userInput.password}
              helperText={
                validated && !userInput.password ? "Please Password." : ""
              }
              sx={{ mb: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <MUILink
              component={RouterLink}
              to="/ForgotPassword"
              sx={{
                color: "#eb3c96",
                textDecoration: "underline",
                display: "block",
                mb: 2,
              }}
            >
              Forgot Password?
            </MUILink>

            <FormControlLabel
              control={
                <Checkbox
                  checked={userInput.rememberMe}
                  onChange={handleRememberMeChange}
                  name="rememberMe"
                />
              }
              label="Remember me"
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#eb3c96",
                "&:hover": { backgroundColor: "#d93585" },
                mb: 2,
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Login"
              )}
            </Button>

            <MUILink
              component={RouterLink}
              to="/register"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                color: "rgba(81, 35, 132, 1)",
                fontWeight: 500,
              }}
            >
              Don’t have an account? Register
            </MUILink>
          </Box>
        </Paper>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}